import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import {Box, IconButton, Paper, Skeleton, SvgIcon, Typography} from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";
import {ReactSortable} from "react-sortablejs";
import {ReactComponent as SortIcon} from '../../../../../../../assets/images/sort-icon.svg';
import Api from "../../../../../../../core/services/api/api";
import useSearch from "../../../../../../hooks/use-search";
import {DefaultPaginationInfo, TaskStatusTypes} from "../../../../../../../core/constants/enums";
import useInfiniteScrollSearchUtils from "../../../../../../hooks/use-infinite-scroll-search-utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import NotebookTaskCard from "../../../../common/notebook/task-card";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import notebookTags from "../../../../../../../core/models/static/routes/notebook-tags";

const NCRTasksViewColumn = forwardRef((props, ref) => {
    const {moveTask, setLoading} = props;

    const [data, setData] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch()

    const sortableListData = useMemo(() => data.map(e => ({...e, filtered: !!removingIds.length})), [data, removingIds.length]);

    /**
     * Searches for list of data for exporting or for showing the list of items in the view
     *
     * * Calls the api regarding this action and updated the pagination info and sets the [reports] value in the inner state if not for
     * exporting Otherwise, returns the crud response.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const response = await Api.getPageTasksV2({
            keyword: args.keywords,
            pagination: args.paginationInfo,
            tags: notebookTags.ncrInformation(),
            sorts: args.orderBy ? [{property: args.orderBy.field, isDesc: args.orderBy.descending}] : null,
            statusId: props.status.id,
        }, args.cancelTokenSource?.token)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const newEntries = response.data?.items ?? [];

            if (!forExport) {
                setData(prevState => [...prevState, ...newEntries]);
                args.setPaginationInfo(prevState => ({...prevState, length: response?.data?.totalCount ?? 0}))
            } else {
                response.data.items = newEntries;
                return response;
            }
        }
    }, [isMounted, props.status.id])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const initialSearchSettings = useMemo(() => ({
        paginationInfo: DefaultPaginationInfo,
        orderBy: undefined,
    }), []);

    const searchData = useSearch(search, initialSearchSettings, false);
    const {setFilters, setPaginationInfo} = searchData;

    /**
     * Provides access of the [setData] setter of this component to its parent.
     */
    useImperativeHandle(ref, () => ({
        ...(ref?.current ?? {}),
        [props.status.id]: {
            setData,
            setPaginationInfo,
        },
    }), [ref, props.status.id, setPaginationInfo])

    /**
     * With each change in the searchData's loading state:
     * - sets the loading state of the status.
     */
    useEffect(() => {
        if (searchData.paginationInfo?.currentPage !== 1) {
            setLoading(props.status.id, false);
        } else {
            setLoading(props.status.id, searchData.loading)
        }
    }, [setLoading, props.status.id, searchData.loading, searchData.paginationInfo?.currentPage]);

    /**
     * With each change in the [query] of props:
     * - resets the data list
     * - applies the keywords in the filters of the [searchData].
     */
    useEffect(() => {
        setFilters?.(props.query)
        setData([]);
    }, [props.query, setFilters]);

    /**
     * Applies the sorting of the column.
     */
    const applySort = useCallback(() => {
        setData([]);
        searchData.setOrderBy?.(orderBy => {
            if (!orderBy) {
                return {descending: true, field: 'Title'};
            }
            if (orderBy.descending) {
                return {descending: false, field: 'Title'};
            }
            return undefined;
        })
    }, [searchData])

    /**
     * finds the task to move and calls the Move task
     */
    const onTaskMoved = useCallback((e) => {
        if (`task-status-${props.status.id}` === e.from.id) {
            const task = data[e.oldDraggableIndex];
            if (!task)
                return;
            moveTask(e, task).then();
        }
    }, [data, moveTask, props.status.id])

    /**
     * Removes the task from the list
     * - Opens a dialog for the user to confirm the action.
     * - If the user confirms the action, the task will be removed from the list.
     */
    const removeTask = useCallback(async (task, close) => {
        close();
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove the selected task?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            async (confirmed) => {
                if (!confirmed)
                    return false;
                setRemovingIds(prevState => [...prevState, task.id]);
                const response = await Api.removeTask(task.id)
                if (!isMounted())
                    return false
                if (response?.resultFlag) {
                    setData(prevState => prevState.filter(e => e.id !== task.id));
                    setPaginationInfo(prevState => ({...prevState, length: (prevState?.length ?? 1) - 1}));
                }
                setRemovingIds(prevState => prevState.filter(e => e !== task.id));
                return response?.resultFlag ?? false
            },
        );
    }, [isMounted, setPaginationInfo])

    /**
     * Toggles the task done status.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleTaskDoneStatus = useCallback(async (task, done) => {
        const forApi = {
            taskId: task?.id,
            taskStatusId: done ? props.statuses.find(e => e.statusType === TaskStatusTypes.Done)?.id : props.statuses.find(e => e.statusType === TaskStatusTypes.ToDo)?.id,
        }
        const response = await Api.changeTaskStatus(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === task.id ? {...e, taskStatus: response?.data} : e));
        }
        return response;
    }, [isMounted, props.statuses])

    const {loadMoreData, hasMoreToLoad} = useInfiniteScrollSearchUtils({searchData, dataLength: data.length});

    /**
     * Sets the task with the received [taskId] to the received [callbackOrTask]
     * @type {(function(number, function | any): void)}
     */
    const setTask = useCallback((taskId, callbackOrTask) => {
        setData(prevState => {
            if (typeof callbackOrTask === 'function') {
                const found = prevState.find(e => e.id === taskId);
                if (!found)
                    return prevState;
                return prevState.map(e => e.id === taskId ? callbackOrTask(found) : e);
            }
            return prevState.map(e => e.id === taskId ? callbackOrTask : e);
        })
    }, [])

    /**
     * Opens the upsert task dialog with the received task
     * @param task
     * @param close
     */
    const onEditTask = useCallback((task, close) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                onSuccess: (task) => setTask(task.id, task),
                task: task,
            }))
        close();
    }, [dispatch, setTask]);

    return (
        <>
            <Paper
                sx={({
                    width: 350,
                    minWidth: 350,
                    height: '80vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column',
                })}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "space-between"}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        {props.status.title ?? ""}
                    </Typography>
                    <IconButton onClick={applySort} color={'paper.primary'}>
                        <SvgIcon
                            component={SortIcon}
                            sx={(theme) => ({
                                transition: theme.transitions.create(['transform', 'opacity', 'color', 'fill', 'stroke']),
                                ...(searchData.orderBy && !searchData.orderBy.descending && {
                                    transform: 'rotate(180deg)',
                                }),
                                ...(!searchData.orderBy && {
                                    opacity: 0.5,
                                })
                            })}/>
                    </IconButton>
                </Box>
                <Box sx={{overflowY: 'auto', flexGrow: 1,}}>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        hasMore={hasMoreToLoad}
                        useWindow={false}
                        threshold={400}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 15,
                            paddingBlock: '1rem',
                            paddingInlineEnd: '0.5rem',
                            ...(searchData.loading ? {} : {minHeight: '100%'}),
                        }}
                    >
                        <ReactSortable
                            id={`task-status-${props.status.id}`}
                            setList={() => void 0}
                            list={sortableListData}
                            onEnd={onTaskMoved}
                            group={'tasks'}
                            animation={210}
                            dragoverBubble
                            disabled={searchData.loading || props.movingTask}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                gap: 15,
                            }}
                        >
                            {
                                data.map(task => (
                                    <NotebookTaskCard
                                        key={task.id}
                                        task={task}
                                        disabled={searchData.loading}
                                        onRemove={removeTask}
                                        onEdit={onEditTask}
                                        toggleTaskDoneStatus={toggleTaskDoneStatus}
                                        setTask={setTask}
                                    />
                                ))
                            }
                        </ReactSortable>
                        {
                            searchData.loading &&
                            Array.from({length: 10}).map((_, key) => (
                                <Skeleton
                                    key={key}
                                    width={'100%'}
                                    sx={{minHeight: 200, transformOrigin: 'none', transform: 'none'}}
                                />
                            ))
                        }
                    </InfiniteScroll>
                </Box>
            </Paper>
        </>
    );
});

export default NCRTasksViewColumn;
